import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { subDays } from 'date-fns';

interface Keyword {
    search_term: string;
    total_organic_searches: any;
}

export interface Item {
    id: string;
    label: string;
    [key: string]: any;
}

export interface ExtraOptions {
    campaigns: Item[];
    ad_groups: Item[];
    search_terms: string[];
    campaign_types: string[];
    accounts: Item[];
    match_types: string[];
    mode: "base" | "activations";
}

interface FilterState {
    brandInfringement: boolean;
    start: string;
    analysisChartCompareStart: string;
    analysisChartCompareEnd: string;
    analysisChartCompareMode:
    | 'none'
    | 'previous_period'
    | 'year_over_year';
    end: string;
    compareStart: string;
    analysisChartStart: string;
    analysisChartEnd: string;
    analysisChartTimestep: string;
    compareEnd: string;
    minDatePicker: string;
    timestep: string;
    devices: string[];
    loadedDevices: string[];
    countries: string[];
    loadedCountries: string[];
    keywords: string[];
    selectedKeywords: any[],
    brandsPLA: string[];
    advertizersPLA: string[];
    notificationBrandInfringementNotification: any,
    notificationBrandInfringement: boolean;
    notificationBrandInfringementNotificationTXT: any;
    dailyDisabled: boolean;
    notificationBrandInfringementTXT: boolean;
    notificationBrandInNonBrandCampaigns: boolean;
    notificationBrandInNonBrandCampaignsFilterEnabled: boolean;
    ratingRangePLA: [number, number];
    pricingRangePLA: [number, number];
    averagePositionPLA: [number, number];
    impressionSharePLA: [number, number];
    keywordsPLA: string[];
    keywordsTopCountries: string[],
    keywordsTrafficAnalysis: string[],
    domainsPLA: string[];
    loadedPlatform: string[];
    keywordTXT: string[];
    brandsTXT: string[],
    advertizerTXT: string[],
    domainsTXT: string[],
    impressionShareTXT: [number, number],
    averagePositionTXT: [number, number],
    keywordsTopCompetitors: string[],
    keywordsTopCompetitorsAds: string[],
    loadedKeywords: Keyword[];
    compare: boolean;
    platforms: string[];
    adType: string[];
    isAdvancedView: boolean;
    campaignType: string[];
    demoMode: boolean;
    autoTimeStep: boolean;
    savingsFormulaFilter: string;
    sinceCrossBrandStart: string,
    extraOptions: ExtraOptions,
    extraOptionsLoaded: ExtraOptions,
    conversionType: string;
    sinceCrossBrand: string | null;
    datesForcedByActivation: boolean,
    searchPerfDateOverridden: boolean,
    loadedCluster: string[],
    tags: { id: string, label: string }[];
    loadedTags: { id: string, label: string }[];

}

const ALIGN_TIMESTEPS = new Set(['year_week', 'year_month']);

const alignStartDateToGranularity = (value?: string, timestep?: string): string | undefined => {
    if (!value || !timestep || !ALIGN_TIMESTEPS.has(timestep)) {
        return value;
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }

    if (timestep === 'year_month') {
        return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1, 12, 0, 0, 0)).toISOString();
    }

    const aligned = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 12, 0, 0, 0));
    const day = aligned.getUTCDay();
    const diffToMonday = day === 0 ? 6 : day - 1;
    aligned.setUTCDate(aligned.getUTCDate() - diffToMonday);
    return aligned.toISOString();
};

const initialState: FilterState = {
    brandInfringement: false,
    // Plage par défaut : les 30 derniers jours, jusqu'à hier (les données du jour
    // courant sont encore incomplètes).
    start: subDays(new Date(), 30).toISOString(),
    end: subDays(new Date(), 1).toISOString(),
    // Période de comparaison par défaut : les 30 jours précédant la plage principale,
    // disjoints (la plage principale commence à J-30, la comparaison finit à J-31).
    compareStart: subDays(new Date(), 60).toISOString(),
    compareEnd: subDays(new Date(), 31).toISOString(),
    savingsFormulaFilter: "m1",
    minDatePicker: '',
    analysisChartCompareStart: '',
    analysisChartCompareEnd: '',
    analysisChartCompareMode: 'none',
    timestep: 'year_week',
    sinceCrossBrandStart: '',
    devices: [],
    loadedDevices: [],
    countries: [],
    analysisChartStart: subDays(new Date(), 13).toISOString(),
    analysisChartEnd: new Date().toISOString(),
    analysisChartTimestep: 'year_week',
    loadedCountries: [],
    loadedPlatform: [],
    keywords: [],
    averagePositionPLA: [0, 50],
    impressionSharePLA: [0, 100],
    keywordsPLA: [],
    notificationBrandInfringementNotification: [],
    notificationBrandInfringementNotificationTXT: [],
    dailyDisabled: false,
    notificationBrandInfringementTXT: false,
    notificationBrandInNonBrandCampaigns: false,
    domainsPLA: [],
    brandsPLA: [],
    selectedKeywords: [],
    ratingRangePLA: [0, 5],
    pricingRangePLA: [0, 50000],
    advertizersPLA: [],
    keywordTXT: [],
    brandsTXT: [],
    advertizerTXT: [],
    domainsTXT: [],
    impressionShareTXT: [0, 100],
    averagePositionTXT: [0, 50],
    notificationBrandInfringement: false,
    notificationBrandInNonBrandCampaignsFilterEnabled: false,
    keywordsTopCountries: [],
    keywordsTrafficAnalysis: [],
    keywordsTopCompetitors: [],
    keywordsTopCompetitorsAds: [],
    loadedKeywords: [],
    compare: false,
    platforms: [],
    adType: [],
    isAdvancedView: false,
    campaignType: [],
    demoMode: false,
    autoTimeStep: true,
    extraOptions: {
        campaigns: [],
        ad_groups: [],
        search_terms: [],
        campaign_types: [],
        accounts: [],
        match_types: [],
        mode: "base"
    },
    extraOptionsLoaded: {
        campaigns: [],
        ad_groups: [],
        search_terms: [],
        campaign_types: [],
        accounts: [],
        match_types: [],
        mode: "base",
    },
    conversionType: "",
    sinceCrossBrand: null,
    datesForcedByActivation: false,
    searchPerfDateOverridden: false,
    loadedCluster: [],
    tags: [],
    loadedTags: [],
};

const filtersSlice = createSlice({
    name: 'filters',
    initialState,
    reducers: {
        setFilterDate: (state, action: PayloadAction<{ start: string; end?: string; skipAlignment?: boolean }>) => {
            const alignedStart = action.payload.skipAlignment
                ? action.payload.start
                : (alignStartDateToGranularity(action.payload.start, state.timestep) ?? action.payload.start);
            state.start = alignedStart;
            state.end = action.payload.end ?? state?.end;
        },
        setAnalysisChartDate: (
            state,
            action: PayloadAction<{
                start: string;
                end: string;
            }>
        ) => {
            state.analysisChartStart = action.payload.start;
            state.analysisChartEnd = action.payload.end;
        },
        setAnalysisChartCompareDate: (
            state,
            action: PayloadAction<{
                compareStart: string;
                compareEnd: string;
            }>
        ) => {
            state.analysisChartCompareStart =
                action.payload.compareStart;

            state.analysisChartCompareEnd =
                action.payload.compareEnd;
        },

        setAnalysisChartCompareMode: (
            state,
            action: PayloadAction<
                'none' |
                'previous_period' |
                'year_over_year'
            >
        ) => {
            state.analysisChartCompareMode =
                action.payload;
        },
        setAnalysisChartTimestep: (
            state,
            action: PayloadAction<string>
        ) => {
            state.analysisChartTimestep = action.payload;
        },
        setFilterDateCompare: (state, action: PayloadAction<{ compareStart: string; compareEnd: string; skipAlignment?: boolean }>) => {
            const alignedCompareStart = action.payload.skipAlignment
                ? action.payload.compareStart
                : (alignStartDateToGranularity(action.payload.compareStart, state.timestep) ?? action.payload.compareStart);
            state.compareStart = alignedCompareStart;
            state.compareEnd = action.payload.compareEnd;
        },
        setFilterMinDatePicker: (state, action: PayloadAction<string>) => {
            state.minDatePicker = action.payload;
        },
        setSelectedKeywords: (state, action: PayloadAction<string[]>) => {
            state.selectedKeywords = action.payload;
        },
        setFilterTimestep: (state, action: PayloadAction<string | { timestep: string; skipAlignment?: boolean }>) => {
            const timestep = typeof action.payload === 'string' ? action.payload : action.payload.timestep;
            const skipAlignment = typeof action.payload === 'object' && action.payload.skipAlignment;
            state.timestep = timestep;
            if (!skipAlignment) {
                state.start = alignStartDateToGranularity(state.start, state.timestep) ?? state.start;
                state.compareStart = alignStartDateToGranularity(state.compareStart, state.timestep) ?? state.compareStart;
            }
        },
        setFilterDevice: (state, action: PayloadAction<string[]>) => {
            state.devices = action.payload;
        },
        setFilterLoadedDevice: (state, action: PayloadAction<string[]>) => {
            state.loadedDevices = action.payload;
        },
        setSavingsFormulaFilter: (state, action: PayloadAction<string>) => {
            state.savingsFormulaFilter = action.payload;
        },
        setFilterCountry: (state, action: PayloadAction<string[]>) => {
            state.countries = action.payload;
        },
        setFilterLoadedCountry: (state, action: PayloadAction<string[]>) => {
            state.loadedCountries = action.payload;
        },
        setFilterKeyword: (state, action: PayloadAction<string[]>) => {
            state.keywords = action.payload;
        },
        setFilterLoadedKeyword: (state, action: PayloadAction<Keyword[]>) => {
            state.loadedKeywords = action.payload;
        },
        resetLoadedKeyword: (state) => {
            state.loadedKeywords = [];
        },
        setFilterCompare: (state, action: PayloadAction<boolean>) => {
            state.compare = action.payload;
        },
        setFilterKeywordTopCountries: (state, action: PayloadAction<string[]>) => {
            state.keywordsTopCountries = action.payload;
        },
        setFilterKeywordTrafficAnalysis: (state, action: PayloadAction<string[]>) => {
            state.keywordsTrafficAnalysis = action.payload;
        },
        setFilterKeywordTopCompetitors: (state, action: PayloadAction<string[]>) => {
            state.keywordsTopCompetitors = action.payload;
        },
        setFilterKeywordTopCompetitorsAds: (state, action: PayloadAction<string[]>) => {
            state.keywordsTopCompetitorsAds = action.payload;
        },
        setFilterKeywordPLA: (state, action: PayloadAction<string[]>) => {
            state.keywordsPLA = action.payload;
        },
        setFilterBrandPLA: (state, action: PayloadAction<string[]>) => {
            state.brandsPLA = action.payload;
        },
        setFilterAdvertizerPLA: (state, action: PayloadAction<string[]>) => {
            state.advertizersPLA = action.payload;
        },
        setFilterRatingRangePLA: (state, action: PayloadAction<[number, number]>) => {
            state.ratingRangePLA = action.payload;
        },
        setFilterPricingRangePLA: (state, action: PayloadAction<[number, number]>) => {
            state.pricingRangePLA = action.payload;
        },
        setFilterDomainPLA: (state, action: PayloadAction<string[]>) => {
            state.domainsPLA = action.payload;
        },
        setFilterKeywordTXT: (state, action: PayloadAction<string[]>) => {
            state.keywordTXT = action.payload;
        },
        setFilterBrandTXT: (state, action: PayloadAction<string[]>) => {
            state.brandsTXT = action.payload;
        },
        setFilterAdvertizerTXT: (state, action: PayloadAction<string[]>) => {
            state.advertizerTXT = action.payload;
        },
        setFilterDomainTXT: (state, action: PayloadAction<string[]>) => {
            state.domainsTXT = action.payload;
        },
        setFilterImpressionShareTXT: (state, action) => {
            state.impressionShareTXT = action.payload;
        },
        setFilterAveragePositionTXT: (state, action) => {
            state.averagePositionTXT = action.payload;
        },
        setNotificationBrandInfringement: (state, action: PayloadAction<boolean>) => {
            state.notificationBrandInfringement = action.payload;
        },
        setNotificationBrandInfringementTXT: (state, action: PayloadAction<boolean>) => {
            state.notificationBrandInfringementTXT = action.payload;
        },
        setNotificationBrandInfringementNotification: (state, action: PayloadAction<any>) => {
            state.notificationBrandInfringementNotification = action.payload;
        },
        setNotificationBrandInfringementNotificationTXT: (state, action: PayloadAction<any>) => {
            state.notificationBrandInfringementNotificationTXT = action.payload;
        },
        setFilterAveragePositionPLA: (state, action) => {
            state.averagePositionPLA = action.payload;
        },
        setFilterImpressionSharePLA: (state, action) => {
            state.impressionSharePLA = action.payload;
        },
        setFilterPlatform: (state, action: PayloadAction<string[]>) => {
            state.platforms = action.payload;
        },
        setFilterAdType: (state, action: PayloadAction<string[]>) => {
            state.adType = action.payload;
        },
        setNotificationBrandInNonBrandCampaigns: (state, action: PayloadAction<boolean>) => {
            state.notificationBrandInNonBrandCampaigns = action.payload;
        },
        setNotificationBrandInNonBrandCampaignsFilterEnabled: (state, action: PayloadAction<boolean>) => {
            state.notificationBrandInNonBrandCampaignsFilterEnabled = action.payload;
        },
        setFilterLoadedPlatform: (state, action: PayloadAction<any>) => {
            state.loadedPlatform = action.payload;
        },
        setFilterDailyDisabled: (state, action: PayloadAction<boolean>) => {
            state.dailyDisabled = action.payload
        },
        setAdvancedView: (state, action: PayloadAction<boolean>) => {
            state.isAdvancedView = action.payload;
        },
        setFilterCampaignType: (state, action: PayloadAction<string[]>) => {
            state.campaignType = action.payload;
        },
        setDemoMode: (state, action: PayloadAction<boolean>) => {
            state.demoMode = action.payload;
        },
        setAutoTimeStep: (state, action: PayloadAction<boolean>) => {
            state.autoTimeStep = action.payload;
        },
        setSinceCrossBrandStart: (state, action: PayloadAction<string>) => {
            state.sinceCrossBrandStart = action.payload;
        },
        setExtraOptions: (state, action: PayloadAction<any>) => {
            state.extraOptions = action.payload;
        },
        setExtraOptionsLoaded: (state, action: PayloadAction<any>) => {
            state.extraOptionsLoaded = action.payload;
        },
        setFilterConversionType: (state, action: PayloadAction<string>) => {
            state.conversionType = action.payload;
        },
        setSinceCrossBrand: (state, action: PayloadAction<string | null>) => {
            state.sinceCrossBrand = action.payload;
        },
        setDatesForcedByActivation: (state, action: PayloadAction<boolean>) => {
            state.datesForcedByActivation = action.payload;
        },
        setSearchPerfDateOverridden: (state, action: PayloadAction<boolean>) => {
            state.searchPerfDateOverridden = action.payload;
        },
        setFilterTags: (state, action: PayloadAction<{ id: string, label: string }[]>) => {
            state.tags = action.payload;
        },
        setFilterLoadedTags: (state, action: PayloadAction<{ id: string, label: string }[]>) => {
            state.loadedTags = action.payload;
        },
    },
});

export const {
    setFilterDate,
    setFilterDevice,
    setFilterLoadedDevice,
    setFilterCountry,
    setFilterLoadedCountry,
    setFilterKeyword,
    setFilterKeywordTopCountries,
    setFilterKeywordTrafficAnalysis,
    setFilterKeywordTopCompetitors,
    setFilterKeywordTopCompetitorsAds,
    setFilterLoadedKeyword,
    resetLoadedKeyword,
    setFilterTimestep,
    setFilterDateCompare,
    setFilterMinDatePicker,
    setFilterCompare,
    setFilterKeywordPLA,
    setFilterBrandPLA,
    setFilterAdvertizerPLA,
    setFilterRatingRangePLA,
    setFilterPricingRangePLA,
    setFilterDomainPLA,
    setFilterKeywordTXT,
    setFilterBrandTXT,
    setFilterAdvertizerTXT,
    setFilterDomainTXT,
    setFilterImpressionShareTXT,
    setFilterAveragePositionTXT,
    setFilterAveragePositionPLA,
    setFilterImpressionSharePLA,
    setNotificationBrandInfringement,
    setNotificationBrandInfringementNotification,
    setNotificationBrandInfringementTXT,
    setNotificationBrandInfringementNotificationTXT,
    setFilterPlatform,
    setFilterAdType,
    setNotificationBrandInNonBrandCampaigns,
    setFilterLoadedPlatform,
    setNotificationBrandInNonBrandCampaignsFilterEnabled,
    setFilterDailyDisabled,
    setAdvancedView,
    setFilterCampaignType,
    setSelectedKeywords,
    setDemoMode,
    setAutoTimeStep,
    setSavingsFormulaFilter,
    setSinceCrossBrandStart,
    setExtraOptions,
    setExtraOptionsLoaded,
    setFilterConversionType,
    setSinceCrossBrand,
    setDatesForcedByActivation,
    setSearchPerfDateOverridden,
    setFilterTags,
    setAnalysisChartDate,
    setAnalysisChartCompareDate,
    setAnalysisChartCompareMode,
    setAnalysisChartTimestep,
    setFilterLoadedTags,
} = filtersSlice.actions;

export default filtersSlice.reducer;